// Long enough for a slow phone to load a lazy drawer, short enough that a tap never opens after a login change.
export const EARLY_OPEN_MAX_AGE_MS = 10_000;

/**
 * Generic event queue that handles queueing and dispatching of custom events
 * when a component is not ready to receive them
 */
export class WindowEventQueue<T> {
  private pendingCalls: { payload: T; queuedAt: number }[] = [];
  private isReady: boolean = false;
  private readonly eventName: string;
  private readonly maxAgeMs?: number;

  /** With maxAgeMs, a pending call older than that is dropped instead of replayed. */
  constructor(eventName: string, options?: { maxAgeMs?: number }) {
    this.eventName = eventName;
    this.maxAgeMs = options?.maxAgeMs;
  }

  /**
   * Call this AFTER event listeners are registered
   */
  setReady(ready: boolean): void {
    this.isReady = ready;

    // Process any pending calls when becoming ready
    if (ready && this.pendingCalls.length > 0) {
      // Process the most recent call (in case there are multiple)
      const latestCall = this.pendingCalls[this.pendingCalls.length - 1];
      this.pendingCalls = []; // Clear the queue
      if (this.maxAgeMs !== undefined && Date.now() - latestCall.queuedAt > this.maxAgeMs) return;
      // Dispatch the event immediately since we're ready
      this.dispatchEvent(latestCall.payload);
    }
  }

  /**
   * Clear pending calls when unmounted
   */
  clearPendingCalls(): void {
    this.isReady = false;
    this.pendingCalls = [];
  }

  /**
   * Add an event call to the queue if Component is not ready, or dispatch immediately if ready
   */
  queueOrDispatch(payload: T): void {
    if (this.isReady) {
      this.dispatchEvent(payload);
    } else {
      this.pendingCalls.push({ payload, queuedAt: Date.now() });
    }
  }

  isEventReady(): boolean {
    return this.isReady;
  }

  getPendingCallsCount(): number {
    return this.pendingCalls.length;
  }

  private dispatchEvent(payload: T): void {
    window.dispatchEvent(
      new CustomEvent(this.eventName, {
        detail: payload,
      }),
    );
  }
}
